/**
 * Warmup routine assembler — the generator's matrix rules, in JS, over the
 * reviewed catalog (v2, 69 exercises). Pure: no React, no Firestore, no audio.
 *
 * A PORT, NOT A REDESIGN. `scripts/warmups/generate.py` (plan_cell, admits,
 * candidate_pool, span_bounds, fit) is the pedagogy Anthony and Grace signed
 * off on, and `src/data/warmupMatrix.json` is a byte-for-byte copy of its
 * `matrix.json` (a test pins that). Every rule below cites the Python it
 * mirrors so the two can be diffed by a human.
 *
 * WHAT DIFFERS, because the catalog does: the generator reads v1
 * (`scripts/warmups/catalog.json`, 32 exercises) and the site reads v2, which
 * Grace reviewed and KEPT WHOLE on 2026-09-03. v2 has no `spanSemitones`,
 * `rhythm`, `syllableMode`, `minLevel` or `patternByStyle`:
 *   - rhythm is inside `pattern` as [degree, beats] pairs — nothing to derive
 *   - `level` IS the minimum level (v1's `minLevel`)
 *   - `styles` / `ages` are space-separated strings ("all" or a list)
 *   - the SPAN (how many semitones a walk climbs) is inherited from the v1
 *     exercise a v2 row came from via `was`, and for the 37 new rows falls
 *     back to a per-purpose default drawn from v1's own medians. Unpitched
 *     patterns are span 0, as in v1. This is the one place the port had to
 *     choose; Grace's review saw no spans, so `exerciseSpan` is the field to
 *     show her next.
 *   - the matrix names v1 exercises by number ("13", "07"); those are mapped
 *     to v2 ids through `was` at load.
 *
 * Purposes: v2 writes "warm-in" / "cool-down"; the matrix writes "warmin" /
 * "cooldown". `normalizePurpose` folds the hyphen so one vocabulary reaches
 * the sequence rules.
 */
import matrix from '../data/warmupMatrix.json';
import { isUnpitched, patternRange, nameToMidi, plannedSteps, exerciseSeconds } from './warmupStepPlan';

export const DURATIONS = ['short', 'mid', 'long'];
export const LEVELS = ['beg', 'int', 'adv'];
export const AGE_BANDS = ['8-10', '11-13', '14-18'];
export const STYLES = ['legit', 'pop', 'belt', 'choral'];
export const VOICES = ['low', 'mid', 'high'];

export const DURATION_WORDS = { short: 'Short · 3–5 min', mid: 'Mid · 10 min', long: 'Long · 12–15 min' };
export const VOICE_WORDS = { low: 'Low (from G3)', mid: 'Mid (from C4)', high: 'High (from E4)' };

const LEVEL_RANK = { beg: 0, int: 1, adv: 2 };

/** v1 `spanSemitones` by v1 number — read off scripts/warmups/catalog.json 2026-09-03. */
export const V1_SPANS = {
	1: 6, 2: 6, 3: 6, 4: 6, 5: 6, 6: 0, 7: 6, 8: 4, 9: 8, 10: 8, 11: 6, 12: 8, 13: 8, 14: 6, 15: 4, 16: 8,
	17: 6, 18: 6, 19: 8, 20: 8, 21: 8, 22: 8, 23: 8, 24: 6, 25: 6, 26: 6, 27: 6, 28: 4, 29: 8, 30: 4, 31: 0, 32: 4,
};

/** Per-purpose default span for v2 rows with no v1 ancestor: v1's median per purpose. */
export const DEFAULT_SPAN_BY_PURPOSE = {
	warmin: 6, breath: 4, registration: 8, resonance: 6, agility: 8, style: 6, diction: 6, exploration: 4, cooldown: 4,
};

export const normalizePurpose = (p) => String(p || '').replace(/-/g, '');

export const exerciseSpan = (ex) => {
	if (isUnpitched(ex.pattern)) return 0;
	if (ex.was != null && V1_SPANS[ex.was] != null) return V1_SPANS[ex.was];
	return DEFAULT_SPAN_BY_PURPOSE[normalizePurpose(ex.purpose)] ?? 6;
};

const list = (v) => String(v || 'all').trim().split(/\s+/);
const stylesOf = (ex) => list(ex.styles);
const agesOf = (ex) => list(ex.ages);

/** cell_exists: some (age × style / level) cells do not exist at all. */
export const cellExists = ({ level, ageBand, style }) => {
	const age = matrix.ages[ageBand];
	if (!age) return false;
	if ((age.excludeStyles || []).includes(style)) return false;
	if ((age.excludeLevels || []).includes(level)) return false;
	return true;
};

/** v1 numbers the matrix names → v2 ids, through `was`. Missing ancestors map to nothing. */
const v1ToV2 = (catalog) => {
	const m = new Map();
	catalog.forEach((ex) => {
		if (ex.was != null) m.set(String(ex.was).padStart(2, '0'), ex.id);
	});
	return (v1id) => m.get(String(v1id).padStart(2, '0')) || null;
};

const startTonicMidi = (voice) => nameToMidi(matrix.voices[voice].startTonic);
const clampRange = (ageBand) => matrix.ages[ageBand].clamp.map(nameToMidi);

/** admits(): does this exercise belong in this cell? level_relax / style_relax loosen the tiers. */
export const admits = (ex, cell, ctx, levelRelax = 0, styleRelax = false) => {
	const age = matrix.ages[cell.ageBand];
	const excluded = (age.excludeExercises || []).map(ctx.toV2).filter(Boolean);
	if (excluded.includes(ex.id)) return false;
	const ages = agesOf(ex);
	if (!ages.includes('all') && !ages.includes(cell.ageBand)) return false;
	if (LEVEL_RANK[ex.level] > LEVEL_RANK[cell.level] + levelRelax) return false;
	const styles = stylesOf(ex);
	if (!styleRelax && !styles.includes('all') && !styles.includes(cell.style)) return false;
	const level = matrix.levels[cell.level];
	const { lo, hi } = patternRange(ex.pattern);
	const exceptions = (level.patternRuleExceptions || []).map(ctx.toV2).filter(Boolean);
	if (level.maxPatternSemitones != null && !exceptions.includes(ex.id) && hi > level.maxPatternSemitones) return false;
	const start = startTonicMidi(cell.voice);
	const [clo, chi] = clampRange(cell.ageBand);
	if (start + hi > chi || start + lo < clo) return false;
	return true;
};

const explicitStyle = (ex, cell) => stylesOf(ex).includes(cell.style);

/** crc32 — the generator seeds rotation with zlib.crc32 of the stem without its voice. */
const CRC_TABLE = (() => {
	const t = new Uint32Array(256);
	for (let n = 0; n < 256; n += 1) {
		let c = n;
		for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
		t[n] = c >>> 0;
	}
	return t;
})();
export const crc32 = (str) => {
	let c = 0xffffffff;
	for (let i = 0; i < str.length; i += 1) c = CRC_TABLE[(c ^ str.charCodeAt(i)) & 0xff] ^ (c >>> 8);
	return (c ^ 0xffffffff) >>> 0;
};

const rotate = (items, seed) => {
	if (!items.length) return items;
	const k = seed % items.length;
	return [...items.slice(k), ...items.slice(0, k)];
};

/** candidate_pool(): ordered (exercise, tier) for a slot, best tier first, rotated by seed within a tier. */
export const candidatePool = (purposes, cell, catalog, ctx, seed) => {
	const byPurpose = [...catalog]
		.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
		.filter((ex) => purposes.has(normalizePurpose(ex.purpose)));
	const tiers = [];
	for (const levelRelax of [0, 1]) {
		const exact = byPurpose.filter((ex) => admits(ex, cell, ctx, levelRelax));
		const name = levelRelax === 0 ? 'exact' : 'level+1';
		tiers.push([name, exact.filter((ex) => explicitStyle(ex, cell))]);
		tiers.push([name, exact.filter((ex) => !explicitStyle(ex, cell))]);
		const relaxed = byPurpose.filter((ex) => admits(ex, cell, ctx, levelRelax, true));
		tiers.push([levelRelax === 0 ? 'style' : 'level+1,style', relaxed]);
	}
	const pool = [];
	const seen = new Set();
	for (const [tier, items] of tiers) {
		for (const ex of rotate(items, seed)) {
			if (!seen.has(ex.id)) {
				seen.add(ex.id);
				pool.push({ ex, tier });
			}
		}
	}
	return pool;
};

const levelBpm = (ex, cell) => Math.round(ex.bpm * matrix.levels[cell.level].tempoMultiplier);

/** span_bounds(): (initial span, min span, max span) after level modifiers, age caps and the clamp. */
export const spanBounds = (ex, cell, ctx) => {
	const catalogSpan = exerciseSpan(ex);
	if (catalogSpan === 0) return { span: 0, minSpan: 0, maxSpan: 0 };
	const level = matrix.levels[cell.level];
	let span = Math.min(catalogSpan, level.spanCap);
	for (const ov of level.spanOverrides || []) {
		const ids = (ov.exercises || []).map(ctx.toV2);
		if ((ov.ages || []).includes(cell.ageBand) && ids.includes(ex.id)) span = ov.span;
	}
	const age = matrix.ages[cell.ageBand];
	const ageCap = age.purposeSpanCaps ? age.purposeSpanCaps[normalizePurpose(ex.purpose)] : undefined;
	const { hi } = patternRange(ex.pattern);
	const [, chi] = clampRange(cell.ageBand);
	const clampMax = chi - (startTonicMidi(cell.voice) + hi);
	let maxSpan = Math.min(catalogSpan + matrix.fitting.maxSpanAboveCatalog, clampMax);
	if (ageCap != null) {
		span = Math.min(span, ageCap);
		maxSpan = Math.min(maxSpan, ageCap);
	}
	span = Math.min(span, maxSpan);
	const minSpan = Math.min(matrix.fitting.minSpan, maxSpan);
	return { span, minSpan, maxSpan };
};

const makeItem = (slot, ex, tier, cell, ctx) => ({
	slot,
	exerciseId: ex.id,
	purpose: normalizePurpose(ex.purpose),
	tier,
	bpm: levelBpm(ex, cell),
	...spanBounds(ex, cell, ctx),
	extraReps: 0,
	fixed: exerciseSpan(ex) === 0,
});

const itemSeconds = (item, byId, first) =>
	exerciseSeconds({ pattern: byId.get(item.exerciseId).pattern, bpm: item.bpm, span: item.span, extraReps: item.extraReps, first }, matrix);

export const routineSeconds = (items, byId) => items.reduce((a, it, i) => a + itemSeconds(it, byId, i === 0), 0);

/** fit(): adjust spans, then cool-down reps, until the plan sits inside its band. Mutates items. Returns in-band. */
export const fit = (items, band, byId) => {
	const [lo, hi] = band;
	const total = () => routineSeconds(items, byId);
	const ok = (t) => t >= lo && t <= hi;
	let t = total();
	if (ok(t)) return true;
	let lastDirection = null;
	// eslint-disable-next-line no-constant-condition
	while (true) {
		const direction = t < lo ? 1 : -1;
		if (lastDirection !== null && direction !== lastDirection) break; // overshoot both ways: hand off to the cool-down
		lastDirection = direction;
		let changed = false;
		let broke = false;
		for (const it of items) {
			if (it.fixed) continue;
			const next = it.span + direction;
			if (next >= it.minSpan && next <= it.maxSpan) {
				it.span = next;
				changed = true;
				t = total();
				if (ok(t)) return true;
				if ((direction === 1 && t > hi) || (direction === -1 && t < lo)) {
					broke = true;
					break;
				}
			}
		}
		if (!changed || broke) {
			if (!changed) break;
			// python: the inner `break` falls through to the next while iteration
			continue;
		}
	}
	const cool = items.find((it) => it.purpose === 'cooldown');
	if (cool) {
		const [loX, hiX] = matrix.fitting.cooldownExtraReps;
		// eslint-disable-next-line no-constant-condition
		while (true) {
			t = total();
			if (ok(t)) return true;
			const direction = t < lo ? 1 : -1;
			const next = cool.extraReps + direction;
			if (next < loX || next > hiX) break;
			const before = plannedSteps(cool.span, cool.extraReps).length;
			cool.extraReps = next;
			if (plannedSteps(cool.span, cool.extraReps).length === before) {
				cool.extraReps -= direction;
				break;
			}
		}
	}
	return ok(total());
};

const decorate = (items, cell, byId) => {
	const start = startTonicMidi(cell.voice);
	return items.map((it, i) => ({
		...it,
		startTonic: start,
		topTonic: start + it.span,
		reps: plannedSteps(it.span, it.extraReps).length,
		seconds: Math.round(itemSeconds(it, byId, i === 0)),
	}));
};

/**
 * plan_cell(): assemble a routine for a cell over the catalog.
 *
 * @param {{duration, level, ageBand, style, voice}} cell
 * @param {Array} catalog  v2 rows (as the portal reads them — `fromStored` shape)
 * @returns {{ cell, items, seconds, band, inBand, error, emptySlots, fallbacks }}
 */
export const assembleRoutine = (cell, catalog) => {
	const byId = new Map(catalog.map((ex) => [ex.id, ex]));
	const ctx = { toV2: v1ToV2(catalog) };
	const band = matrix.bands[cell.duration];
	const seed = crc32(`${cell.duration}_${cell.level}_${cell.ageBand}_${cell.style}`);
	const items = [];
	const emptySlots = [];
	const used = new Set();
	if (!cellExists(cell)) {
		return { cell, items: [], seconds: 0, band, inBand: false, error: `No ${cell.style} routine for ${cell.ageBand}`, emptySlots: [], fallbacks: [] };
	}
	for (const slot of matrix.sequences[cell.duration]) {
		const purposes = new Set(slot.split('|'));
		const pick = candidatePool(purposes, cell, catalog, ctx, seed).find(({ ex }) => !used.has(ex.id));
		if (!pick) {
			emptySlots.push(slot);
			continue;
		}
		used.add(pick.ex.id);
		items.push(makeItem(slot, pick.ex, pick.tier, cell, ctx));
	}
	if (cell.duration === 'short' && routineSeconds(items, byId) < matrix.shortCooldownIfUnderSeconds) {
		const pick = candidatePool(new Set(['cooldown']), cell, catalog, ctx, seed).find(({ ex }) => !used.has(ex.id));
		if (pick) items.push(makeItem('cooldown', pick.ex, pick.tier, cell, ctx));
	}
	if (!items.length) {
		return { cell, items: [], seconds: 0, band, inBand: false, error: 'No exercises admitted for any slot', emptySlots, fallbacks: [] };
	}
	const inBand = fit(items, band, byId);
	const seconds = Math.round(routineSeconds(items, byId));
	return {
		cell,
		items: decorate(items, cell, byId),
		seconds,
		band,
		inBand,
		error: inBand ? null : `Computed ${seconds}s is outside ${band[0]}–${band[1]}s after fitting`,
		emptySlots,
		fallbacks: items.filter((it) => it.tier !== 'exact').map((it) => `${it.exerciseId}:${it.tier}`),
	};
};

/**
 * The Swap picker's list for one slot: every exercise the slot admits, best
 * tier first, minus the ones already in the routine. Same pool the assembler
 * drew from, so a swap keeps the pedagogy.
 */
export const swapCandidates = (routine, index, catalog) => {
	const { cell } = routine;
	const ctx = { toV2: v1ToV2(catalog) };
	const seed = crc32(`${cell.duration}_${cell.level}_${cell.ageBand}_${cell.style}`);
	const item = routine.items[index];
	const purposes = new Set(item.slot.split('|'));
	const used = new Set(routine.items.filter((_, i) => i !== index).map((it) => it.exerciseId));
	return candidatePool(purposes, cell, catalog, ctx, seed).filter(({ ex }) => !used.has(ex.id));
};

/** Replace one slot's exercise and re-fit the whole routine. Returns a NEW routine. */
export const swapExercise = (routine, index, exerciseId, catalog) => {
	const byId = new Map(catalog.map((ex) => [ex.id, ex]));
	const ctx = { toV2: v1ToV2(catalog) };
	const ex = byId.get(exerciseId);
	if (!ex) throw new Error(`Unknown exercise ${exerciseId}`);
	const items = routine.items.map((it, i) => {
		const base = i === index ? makeItem(it.slot, ex, 'swap', routine.cell, ctx) : { ...it, extraReps: it.extraReps || 0 };
		// strip decorations so fit() sees the plain item shape
		const { startTonic, topTonic, reps, seconds, ...plain } = base;
		return plain;
	});
	const inBand = fit(items, routine.band, byId);
	const seconds = Math.round(routineSeconds(items, byId));
	return { ...routine, items: decorate(items, routine.cell, byId), seconds, inBand, error: inBand ? null : `Computed ${seconds}s is outside ${routine.band[0]}–${routine.band[1]}s after fitting` };
};

/** Drop one slot (a skip) and re-fit. */
export const removeExercise = (routine, index, catalog) => {
	const byId = new Map(catalog.map((ex) => [ex.id, ex]));
	const items = routine.items
		.filter((_, i) => i !== index)
		.map(({ startTonic, topTonic, reps, seconds, ...plain }) => plain);
	const inBand = items.length ? fit(items, routine.band, byId) : false;
	const seconds = Math.round(routineSeconds(items, byId));
	return { ...routine, items: decorate(items, routine.cell, byId), seconds, inBand, error: inBand ? null : `Computed ${seconds}s is outside ${routine.band[0]}–${routine.band[1]}s after fitting` };
};

/** Age band from a student's stored age, when there is one. */
export const ageBandFor = (age) => {
	// null / undefined / '' are "unknown", never 0 — Number(null) is 0, which
	// would file every student without an age under 8–10.
	if (age == null || age === '') return null;
	const n = Number(age);
	if (!Number.isFinite(n)) return null;
	if (n <= 10) return '8-10';
	if (n <= 13) return '11-13';
	return '14-18';
};
